import { isDefined, isNotDefined, labelForDateRange, type DateInput, type ReportDateRange } from '@terros/common'
import { fromDateInput } from '../time'

/**
 * Semantic date-range choices, grouped the way they are presented to the viewer.
 * These map straight onto `ReportDateRange` values rather than round-tripping
 * through a concrete `[start, end]` pair, so "Past 30 Days" stays relative
 * instead of freezing into the dates it happened to resolve to when picked.
 */
export type DateRangeOptionGroup = 'rolling' | 'calendar' | 'other'

export type DateRangeOptionKey =
  | 'past_7_days'
  | 'past_30_days'
  | 'past_90_days'
  | 'past_180_days'
  | 'past_3_months'
  | 'past_6_months'
  | 'past_1_year'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'year_to_date'
  | 'all_time'
  | 'custom'

export type DateRangeOption = {
  key: DateRangeOptionKey
  label: string
  group: DateRangeOptionGroup
  /** The value this option writes. `undefined` for `custom`, which needs dates. */
  value?: ReportDateRange
}

export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  { key: 'past_7_days', label: 'Past 7 Days', group: 'rolling', value: { type: 'relative', value: 7, unit: 'days' } },
  {
    key: 'past_30_days',
    label: 'Past 30 Days',
    group: 'rolling',
    value: { type: 'relative', value: 30, unit: 'days' },
  },
  {
    key: 'past_90_days',
    label: 'Past 90 Days',
    group: 'rolling',
    value: { type: 'relative', value: 90, unit: 'days' },
  },
  {
    key: 'past_180_days',
    label: 'Past 180 Days',
    group: 'rolling',
    value: { type: 'relative', value: 180, unit: 'days' },
  },
  {
    key: 'past_3_months',
    label: 'Past 3 Months',
    group: 'rolling',
    value: { type: 'relative', value: 3, unit: 'months' },
  },
  {
    key: 'past_6_months',
    label: 'Past 6 Months',
    group: 'rolling',
    value: { type: 'relative', value: 6, unit: 'months' },
  },
  {
    key: 'past_1_year',
    label: 'Past 1 Year',
    group: 'rolling',
    value: { type: 'relative', value: 12, unit: 'months' },
  },
  { key: 'today', label: 'Today', group: 'calendar', value: { type: 'preset', period: 'today' } },
  { key: 'yesterday', label: 'Yesterday', group: 'calendar', value: { type: 'preset', period: 'yesterday' } },
  { key: 'this_week', label: 'This Week', group: 'calendar', value: { type: 'preset', period: 'this_week' } },
  { key: 'last_week', label: 'Last Week', group: 'calendar', value: { type: 'preset', period: 'last_week' } },
  { key: 'this_month', label: 'This Month', group: 'calendar', value: { type: 'preset', period: 'this_month' } },
  { key: 'last_month', label: 'Last Month', group: 'calendar', value: { type: 'preset', period: 'last_month' } },
  { key: 'this_quarter', label: 'This Quarter', group: 'calendar', value: { type: 'preset', period: 'this_quarter' } },
  { key: 'last_quarter', label: 'Last Quarter', group: 'calendar', value: { type: 'preset', period: 'last_quarter' } },
  { key: 'this_year', label: 'This Year', group: 'calendar', value: { type: 'preset', period: 'this_year' } },
  { key: 'last_year', label: 'Last Year', group: 'calendar', value: { type: 'preset', period: 'last_year' } },
  { key: 'year_to_date', label: 'Year to Date', group: 'calendar', value: { type: 'preset', period: 'year_to_date' } },
  { key: 'all_time', label: 'All Time', group: 'other', value: { type: 'preset', period: 'all_time' } },
  { key: 'custom', label: 'Custom…', group: 'other' },
]

export const DATE_RANGE_OPTION_GROUP_LABELS: Map<DateRangeOptionGroup, string> = new Map([
  ['rolling', 'Rolling Range'],
  ['calendar', 'Calendar Period'],
  ['other', 'Other'],
])

/**
 * Which option a stored range corresponds to, or `undefined` when it is a fixed
 * or advanced range that no preset describes — those read as a custom range.
 * `31 days` matches "Past 30 Days" because that is what a month-long relative
 * range has historically been stored as.
 */
export function findDateRangeOption(range: ReportDateRange | undefined): DateRangeOption | undefined {
  if (!isDefined(range)) return undefined
  if (range.type === 'relative' && range.value === 31 && range.unit === 'days') {
    return DATE_RANGE_OPTIONS.find((option) => option.key === 'past_30_days')
  }
  return DATE_RANGE_OPTIONS.find((option) => isDefined(option.value) && isSameReportDateRange(option.value, range))
}

export function isSameReportDateRange(left: ReportDateRange, right: ReportDateRange): boolean {
  if (left.type !== right.type) return false
  if (left.type === 'preset' && right.type === 'preset') return left.period === right.period
  if (left.type === 'relative' && right.type === 'relative')
    return left.value === right.value && left.unit === right.unit
  if (left.type === 'fixed' && right.type === 'fixed')
    return (
      fromDateInput(left.startTime).toMillis() === fromDateInput(right.startTime).toMillis() &&
      fromDateInput(left.endTime).toMillis() === fromDateInput(right.endTime).toMillis()
    )
  return false
}

export function getDateRangeOptionsByGroup(group: DateRangeOptionGroup): DateRangeOption[] {
  return DATE_RANGE_OPTIONS.filter((option) => option.group === group)
}

/**
 * Display label for a stored range: the matching option's label, the dates
 * themselves for a fixed range that no option describes, and the shared
 * `labelForDateRange` for anything else (odd relative ranges, advanced ones).
 */
export function stringifyReportDateRange(range: ReportDateRange | undefined): string | undefined {
  if (isNotDefined(range)) return undefined

  const option = findDateRangeOption(range)
  if (isDefined(option)) return option.label
  if (range.type === 'fixed') return `${formatDay(range.startTime)} – ${formatDay(range.endTime)}`
  return labelForDateRange(range)
}

function formatDay(time: DateInput): string {
  return fromDateInput(time).toFormat('LLL d, yyyy')
}
